import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/shared";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

const GREEN = "#35A787";
const CATEGORIES = ["Restaurant", "Cafe", "Gym / Fitness", "Retail", "Salon / Spa", "Real Estate", "Education", "Healthcare", "Events", "Other"];

export default function AdvertiserSignup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", company: "", category: "Restaurant" });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (f.password.length < 6) return setErr("Password must be at least 6 characters");
    setBusy(true);
    const r = await register("advertiser", f);
    setBusy(false);
    if (r.ok) navigate("/business/overview");
    else setErr(r.error);
  };

  const input = "w-full rounded-lg border border-[#D8D6CF] bg-white px-3 py-2.5 text-sm text-[#1F2937] outline-none transition-colors placeholder:text-[#9CA3AF] focus:border-[#35A787] focus:ring-2 focus:ring-[#35A787]/20";
  const label = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#5B5A56]";

  return (
    <div className="min-h-screen bg-[#FBFAF7]">
      <header className="flex h-16 items-center justify-between border-b border-[#EAE9E4] bg-white px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2.5">
          <Logo size={26} className="max-w-[130px] object-contain" />
          <span className="font-display text-base font-bold tracking-tight text-[#14130E]">Anand</span>
        </Link>
        <Link to="/signup/driver" className="text-sm font-medium text-[#35A787] transition-colors hover:text-[#2D8F74]">I'm a driver →</Link>
      </header>

      <div className="mx-auto max-w-md px-4 py-10 sm:py-14">
        <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-xs font-medium text-[#8A887F] transition-colors hover:text-[#14130E]"><ArrowLeft size={14} /> Back to home</Link>
        <h1 className="font-display text-2xl font-bold tracking-tight text-[#14130E]">Create your advertiser account</h1>
        <p className="mt-1.5 text-sm text-[#6B6659]">Get on air in minutes. Your account is active the moment you sign up.</p>

        <form onSubmit={submit} className="mt-6 space-y-4 rounded-2xl border border-[#EAE9E4] bg-white p-6 shadow-[0_1px_3px_rgba(16,24,40,0.06)]">
          <div>
            <label className={label}>Contact name</label>
            <input className={input} value={f.name} onChange={set("name")} placeholder="e.g. Priya Sharma" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Email</label>
              <input className={input} type="email" value={f.email} onChange={set("email")} placeholder="you@company.com" required />
            </div>
            <div>
              <label className={label}>Phone</label>
              <input className={input} type="tel" value={f.phone} onChange={set("phone")} placeholder="10-digit mobile" pattern="[0-9]{10}" required />
            </div>
          </div>
          <div>
            <label className={label}>Company / business name</label>
            <input className={input} value={f.company} onChange={set("company")} placeholder="Your business name" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Category</label>
              <select className={input} value={f.category} onChange={set("category")}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Password</label>
              <input className={input} type="password" value={f.password} onChange={set("password")} placeholder="Min 6 characters" minLength={6} required />
            </div>
          </div>

          {err && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">{err}</div>}

          <button type="submit" disabled={busy} className="tap flex w-full items-center justify-center gap-2 rounded-xl bg-[#35A787] px-5 py-3.5 text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Creating account…</> : "Create account"}
          </button>
          <p className="text-center text-[11px] leading-relaxed text-[#8A887F]">
            By signing up you agree to our terms. Ad creatives are reviewed by ops before going live.
          </p>
        </form>

        <ul className="mt-5 space-y-2 text-sm text-[#4B5563]">
          <li className="flex items-start gap-2"><CheckCircle2 size={16} className="mt-0.5 flex-none text-[#35A787]" /> No approval wait — log straight into your dashboard</li>
          <li className="flex items-start gap-2"><CheckCircle2 size={16} className="mt-0.5 flex-none text-[#35A787]" /> Upload your first ad and pick cabs right away</li>
        </ul>
      </div>
    </div>
  );
}